import React from "react";
import { Paper, Typography, Box, CircularProgress } from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import {
  InvoiceSummary,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
} from "../types/invoice";

interface StatsSummaryProps {
  stats: InvoiceSummary[];
  loading: boolean;
}

const StatsSummary: React.FC<StatsSummaryProps> = ({ stats, loading }) => {
  const getCategoryIcon = (categoria: string) => {
    switch (categoria) {
      case "regular":
        return <ReceiptIcon />;
      case "a_faturar":
        return <BusinessIcon />;
      case "despesa":
        return <TrendingDownIcon />;
      case "despesa_a_faturar":
        return <AccountBalanceIcon />;
      default:
        return <ReceiptIcon />;
    }
  };

  const totalInvoices = stats.reduce(
    (sum, stat) => sum + (Number(stat.total_faturas) || 0),
    0
  );
  const totalValue = stats.reduce(
    (sum, stat) => sum + (Number(stat.valor_total) || 0),
    0
  );

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Resumo Estatístico
      </Typography>

      {/* Totals */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: "1 1 200px", textAlign: "center" }}>
            <Typography variant="h4" color="primary">
              {totalInvoices}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total de Faturas
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 200px", textAlign: "center" }}>
            <Typography variant="h4" color="primary">
              €{(totalValue || 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Valor Total
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 200px", textAlign: "center" }}>
            <Typography variant="h4" color="primary">
              {stats.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Categorias Ativas
            </Typography>
          </Box>
          <Box sx={{ flex: "1 1 200px", textAlign: "center" }}>
            <Typography variant="h4" color="primary">
              €
              {totalInvoices > 0
                ? ((totalValue || 0) / totalInvoices).toFixed(2)
                : "0.00"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Valor Médio
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Category breakdown */}
      {stats.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Por Categoria
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {stats.map((stat) => (
              <Box
                key={stat.categoria}
                sx={{
                  flex: "1 1 250px",
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Box display="flex" alignItems="center" mb={1}>
                  <Box
                    sx={{
                      color:
                        CATEGORY_COLORS[
                          stat.categoria as keyof typeof CATEGORY_COLORS
                        ],
                      mr: 1,
                    }}
                  >
                    {getCategoryIcon(stat.categoria)}
                  </Box>
                  <Typography variant="subtitle2">
                    {
                      CATEGORY_LABELS[
                        stat.categoria as keyof typeof CATEGORY_LABELS
                      ]
                    }
                  </Typography>
                </Box>
                <Typography variant="h6">
                  {Number(stat.total_faturas) || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  €{(Number(stat.valor_total) || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Média: €{(Number(stat.valor_medio) || 0).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {stats.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            Nenhuma fatura encontrada para o período selecionado
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default StatsSummary;
